import {
  AdditiveBlending,
  BufferAttribute,
  BufferGeometry,
  CircleGeometry,
  CylinderGeometry,
  DoubleSide,
  Group,
  LinearFilter,
  Mesh,
  MeshStandardMaterial,
  Object3D,
  PlaneGeometry,
  Points,
  ShaderMaterial,
  Texture,
  TextureLoader,
} from 'three'
import {BaseResourceLoader} from './BaseResourceLoader'
import {BILLBOARD_CONSTANTS} from './DataOfThreeJS/BillboardLoader/BillboardConstants'
import {IUniformsUpdatable} from './interfaces/IObjectLoader'
import {UniformsUpdater} from './utils/UniformsUpdater'
import {
  billboardTrailFragmentShader,
  billboardTrailVertexShader,
} from '@/components/MapContext/ThreeJs/DataOfThreeJS/BillboardLoader/BillboardShaders'

export interface BillboardObject3D extends Object3D {
  hexId?: string
}

export default class BillboardLoader
  extends BaseResourceLoader
  implements IUniformsUpdatable
{
  private billboardTexture: Texture | null = null
  private readonly texturePath = BILLBOARD_CONSTANTS.DEFAULT_TEXTURE_PATH

  constructor() {
    super()
    this.loadTexture(this.texturePath, {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      generateMipmaps: false,
      flipY: true,
    })
      .then((texture) => {
        this.billboardTexture = texture
      })
      .catch((error) => {
        console.error('Failed to load billboard texture', error)
      })
  }

  async waitForLoad(): Promise<void> {
    await this.waitForResource(this.texturePath)
  }

  /**
   * Получение billboards по строковому ключу (для обратной совместимости)
   * Возвращает readonly Record для защиты от изменений извне
   */
  get billboards(): Readonly<Record<string, BillboardObject3D>> {
    return this.getObjectsAsRecord<BillboardObject3D>()
  }

  /**
   * Обновление uniforms всех billboard точек (вызывается из ThreeJsLayer перед рендерингом)
   */
  updateUniforms(currentTime: number): void {
    this.objectsMap.forEach((billboard) => {
      UniformsUpdater.updateUniformsInPoints(billboard, currentTime, ['uTime'])
    })
  }

  clearAllBillboards(): void {
    this.clearAll()
  }

  private loadImageForTexture(
    fullImageUrl: string,
    material: MeshStandardMaterial
  ): void {
    new TextureLoader().load(fullImageUrl, (texture) => {
      texture.flipY = true
      material.map = texture
      material.needsUpdate = true
    })
  }

  private createBillboardGroup(
    imageUrl: string | undefined,
    isPreview: boolean = false
  ): Group | null {
    if (!this.threeLayer) {
      return null
    }

    const group = new Group()

    const poleGeometry = new CylinderGeometry(
      BILLBOARD_CONSTANTS.POLE_RADIUS,
      BILLBOARD_CONSTANTS.POLE_RADIUS,
      BILLBOARD_CONSTANTS.POLE_HEIGHT,
      8
    )
    const poleMaterial = new MeshStandardMaterial({color: 0x8b7355})
    const pole = new Mesh(poleGeometry, poleMaterial)
    pole.position.y = BILLBOARD_CONSTANTS.POLE_HEIGHT / 2
    group.add(pole)

    const boardGeometry = new PlaneGeometry(
      BILLBOARD_CONSTANTS.BOARD_WIDTH,
      BILLBOARD_CONSTANTS.BOARD_HEIGHT
    )

    const boardMaterial = new MeshStandardMaterial({
      map: this.billboardTexture,
      side: DoubleSide,
      transparent: true,
    })

    if (imageUrl) {
      const imagePath = imageUrl.startsWith('/') ? imageUrl : `/${imageUrl}`
      const fullImageUrl =
        process.env.NODE_ENV === 'development'
          ? `/share${imagePath}`
          : `${process.env.REACT_APP_SHARE_BASE_URL}${imagePath}`

      this.loadImageForTexture(fullImageUrl, boardMaterial)
    }
    const board = new Mesh(boardGeometry, boardMaterial)
    board.position.y =
      BILLBOARD_CONSTANTS.POLE_HEIGHT + BILLBOARD_CONSTANTS.BOARD_HEIGHT / 2
    group.add(board)

    const baseGeometry = new CircleGeometry(
      BILLBOARD_CONSTANTS.BASE_RADIUS,
      BILLBOARD_CONSTANTS.BASE_SEGMENTS
    )
    const baseMaterial = new MeshStandardMaterial({
      color: 0x000000,
    })
    const base = new Mesh(baseGeometry, baseMaterial)
    base.rotation.x = -Math.PI / 2
    base.position.y = 0.05
    group.add(base)

    const pointsSystem = this.createTrailPointsSystem()
    if (pointsSystem) {
      pointsSystem.position.y = 0.1
      group.add(pointsSystem)
    }

    return group
  }

  private createTrailPointsSystem(): Points | null {
    const vertexShader = billboardTrailVertexShader
    const fragmentShader = billboardTrailFragmentShader

    const totalPoints = BILLBOARD_CONSTANTS.TRAIL_POINTS * 2

    const geometry = new BufferGeometry()
    const indices = new Float32Array(totalPoints)
    const sides = new Float32Array(totalPoints)

    for (let i = 0; i < totalPoints; i++) {
      indices[i] = i % BILLBOARD_CONSTANTS.TRAIL_POINTS
      sides[i] = i < BILLBOARD_CONSTANTS.TRAIL_POINTS ? 0 : 1
    }

    geometry.setAttribute('aIndex', new BufferAttribute(indices, 1))
    geometry.setAttribute('aSide', new BufferAttribute(sides, 1))
    geometry.setAttribute(
      'position',
      new BufferAttribute(new Float32Array(totalPoints * 3), 3)
    )

    const material = new ShaderMaterial({
      uniforms: {
        uTime: {value: 0},
        uRadius: {value: BILLBOARD_CONSTANTS.RADIUS},
        uGlowSize: {value: BILLBOARD_CONSTANTS.GLOW_SIZE},
        uGlowLength: {value: BILLBOARD_CONSTANTS.GLOW_LENGTH},
        uPointScale: {value: BILLBOARD_CONSTANTS.POINT_SCALE},
      },
      vertexShader,
      fragmentShader,
      transparent: true,
      blending: AdditiveBlending,
      depthWrite: false,
    })

    return new Points(geometry, material)
  }

  addBillboard(
    center: [number, number],
    hexId: string,
    imageUrl?: string
  ): BillboardObject3D | null {
    if (!this.hasThreeLayer()) {
      return null
    }

    const group = this.createBillboardGroup(imageUrl, false)
    if (!group) {
      return null
    }

    const addedGroup = this.threeLayer.addObject(
      hexId,
      group,
      center,
      0,
      1
    ) as BillboardObject3D

    addedGroup.hexId = hexId
    addedGroup.name = `billboard-${hexId}`
    this.addObjectToCollection(hexId, addedGroup)

    return addedGroup
  }

  createPreviewBillboard(
    center: [number, number],
    imageUrl?: string
  ): Object3D | null {
    if (!this.hasThreeLayer()) {
      return null
    }

    const group = this.createBillboardGroup(imageUrl, true)
    if (!group) {
      return null
    }

    const previewId = `preview-billboard-${Date.now()}`
    return this.threeLayer.addObject(previewId, group, center, 0, 1)
  }
}
